// Birthday utilities — compute "is birthday today" in a family timezone,
// and format birth_date strings (YYYY-MM-DD; year may be 1900 if unknown).

export function getTodayMonthDay(timezone?: string): { month: number; day: number; year: number; ymd: string } {
  const now = new Date();
  let parts: { month: number; day: number; year: number };
  try {
    const tz = timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
    const fmt = new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
    const p = fmt.formatToParts(now).reduce<Record<string, string>>((acc, x) => {
      if (x.type !== 'literal') acc[x.type] = x.value;
      return acc;
    }, {});
    parts = { month: parseInt(p.month, 10), day: parseInt(p.day, 10), year: parseInt(p.year, 10) };
  } catch {
    parts = { month: now.getMonth() + 1, day: now.getDate(), year: now.getFullYear() };
  }
  const ymd = `${parts.year}-${String(parts.month).padStart(2, '0')}-${String(parts.day).padStart(2, '0')}`;
  return { month: parts.month, day: parts.day, year: parts.year, ymd };
}

export function isBirthdayToday(birthDate?: string | null, timezone?: string): boolean {
  if (!birthDate) return false;
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(birthDate);
  if (!m) return false;
  const month = parseInt(m[2], 10);
  const day = parseInt(m[3], 10);
  const today = getTodayMonthDay(timezone);
  return month === today.month && day === today.day;
}

// Sentinel year used when the user does not know the birth year.
// Must be >= 1900 to satisfy the DB validation trigger on family_members.birth_date.
export const UNKNOWN_BIRTH_YEAR = 1900;

export function hasBirthYear(birthDate?: string | null): boolean {
  if (!birthDate) return false;
  const m = /^(\d{4})-/.exec(birthDate);
  if (!m) return false;
  const y = parseInt(m[1], 10);
  return y > UNKNOWN_BIRTH_YEAR;
}

export function getEffectiveBirthDate(
  birthDate?: string | null,
  legacyBirthday?: string | null,
): string | null {
  if (birthDate && hasBirthYear(birthDate)) return birthDate;
  if (legacyBirthday && hasBirthYear(legacyBirthday)) return legacyBirthday;
  return birthDate ?? legacyBirthday ?? null;
}

export function ageTurningToday(birthDate?: string | null, timezone?: string): number | null {
  if (!hasBirthYear(birthDate)) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(birthDate!);
  if (!m) return null;
  const birthYear = parseInt(m[1], 10);
  const today = getTodayMonthDay(timezone);
  return today.year - birthYear;
}

// ----- Generic helpers used by <BirthdayBadge /> -----
// These accept a date string ("YYYY-MM-DD", year may be 0001 for unknown),
// a Date object, null or undefined.

function parseBday(birthday: string | Date | null | undefined): Date | null {
  if (!birthday) return null;
  if (birthday instanceof Date) return isNaN(birthday.getTime()) ? null : birthday;
  // Accept "YYYY-MM-DD" or full ISO; normalise.
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(birthday);
  if (m) {
    const y = Math.max(parseInt(m[1], 10), 2000);
    const d = new Date(y, parseInt(m[2], 10) - 1, parseInt(m[3], 10));
    return isNaN(d.getTime()) ? null : d;
  }
  const d = new Date(birthday);
  return isNaN(d.getTime()) ? null : d;
}

function todayInTz(timezone: string = 'UTC'): Date {
  try {
    const now = new Date();
    return new Date(now.toLocaleString('en-US', { timeZone: timezone }));
  } catch {
    return new Date();
  }
}

export function daysUntilBirthday(
  birthday: string | Date | null | undefined,
  timezone: string = 'UTC',
): number | null {
  const bday = parseBday(birthday);
  if (!bday) return null;
  const today = todayInTz(timezone);
  const todayMidnight = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const next = new Date(today.getFullYear(), bday.getMonth(), bday.getDate());
  if (next < todayMidnight) next.setFullYear(next.getFullYear() + 1);
  const diff = next.getTime() - todayMidnight.getTime();
  return Math.round(diff / (1000 * 60 * 60 * 24));
}

export function getAge(birthday: string | Date | null | undefined): number | null {
  const bday = parseBday(birthday);
  if (!bday) return null;
  if (!hasBirthYear(typeof birthday === 'string' ? birthday : null)) {
    // For string inputs without a known year, age is unknown.
    if (typeof birthday === 'string') return null;
  }
  const now = new Date();
  let age = now.getFullYear() - bday.getFullYear();
  const m = now.getMonth() - bday.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < bday.getDate())) age--;
  return age >= 0 ? age : null;
}

export function isBirthdayWithinDays(
  birthday: string | Date | null | undefined,
  days: number,
  timezone: string = 'UTC',
): boolean {
  const remaining = daysUntilBirthday(birthday, timezone);
  if (remaining === null) return false;
  return remaining <= days;
}

// Date <-> string helpers for the picker. We accept a Date and a "yearKnown" flag.
export function dateToBirthString(date: Date, yearKnown: boolean): string {
  const y = yearKnown ? date.getFullYear() : UNKNOWN_BIRTH_YEAR;
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const dd = String(date.getDate()).padStart(2, '0');
  return `${String(y).padStart(4, '0')}-${mm}-${dd}`;
}

export function birthStringToDate(s?: string | null): Date | undefined {
  if (!s) return undefined;
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  if (!m) return undefined;
  const y = parseInt(m[1], 10);
  return new Date(y, parseInt(m[2], 10) - 1, parseInt(m[3], 10));
}
